import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAppSelector } from '../redux/hooks';
import { useGetSiteArtefactsQuery, useGetArtefactViewUrlMutation, type Artefact } from '../redux/api/portfolioApi';
import { useGetQuestionSummaryQuery } from '../redux/api/qaApi';
import { getCachedPDF, cachePDF } from '../services/pdfCacheService';
import { pageNavigationPlugin } from '@react-pdf-viewer/page-navigation';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';

export interface PageRef {
  label: string;
  page: number;
  type?: string;
}

export interface SelectedComponentInfo {
  nodeId: string;
  nodeLabel?: string;
  pageRefs: PageRef[];
}

export interface BoundingBoxOverlay {
  x_min: number;
  y_min: number;
  x_max: number;
  y_max: number;
  answer_type: string; // 'pass' | 'fail' | 'ca'
  description?: string;
  question_id: string;
  category?: string;
}

export interface UseAsBuiltViewerReturn {
  // Site data
  currentSite: ReturnType<typeof useAppSelector<(state: any) => any>>;
  tenantId: string | undefined;
  siteId: string | undefined;

  // Artefact data
  asBuiltArtefact: Artefact | null;
  isLoadingArtefacts: boolean;
  artefactsError: any;

  // PDF state
  pdfViewUrl: string | null;
  isLoadingViewUrl: boolean;
  viewerError: string | null;
  isFromCache: boolean;

  // Page navigation
  currentPage: number;
  totalPages: number;
  displayPage: number;
  pageRefs: PageRef[];
  goToPage: (page: number) => void;

  // Component selection
  selectedComponent: SelectedComponentInfo | null;
  clearSelectedComponent: () => void;

  // Viewer controls
  handlePageChange: (e: { currentPage: number }) => void;
  handleDocumentLoad: (e: { doc: { numPages: number } }) => void;

  // Plugin instances
  defaultLayoutPluginInstance: ReturnType<typeof defaultLayoutPlugin>;
  pageNavigationPluginInstance: ReturnType<typeof pageNavigationPlugin>;

  // Bounding boxes
  boundingBoxMap: Record<string, BoundingBoxOverlay[]>;
  showBoundingBoxes: boolean;
  toggleBoundingBoxes: () => void;
  isFetchingBBoxes: boolean;
  highlightedBBox: { pageNumber: number; bbox: { x_min: number; y_min: number; x_max: number; y_max: number } } | null;

  // Download progress (0-100, null when not downloading)
  downloadProgress: number | null;

  // Loading state
  isLoading: boolean;
}

export function useAsBuiltViewer(): UseAsBuiltViewerReturn {
  // State
  const [viewerError, setViewerError] = useState<string | null>(null);
  const [pdfViewUrl, setPdfViewUrl] = useState<string | null>(null);
  const [downloadProgress, setDownloadProgress] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [pageRefs, setPageRefs] = useState<PageRef[]>([]);
  const [selectedComponent, setSelectedComponent] = useState<SelectedComponentInfo | null>(null);
  const [isFromCache, setIsFromCache] = useState(false);

  // Bounding box state
  const [showBoundingBoxes, setShowBoundingBoxes] = useState(false);
  const [highlightedBBox, setHighlightedBBox] = useState<{ pageNumber: number; bbox: { x_min: number; y_min: number; x_max: number; y_max: number } } | null>(null);

  // Refs
  const blobUrlRef = useRef<string | null>(null);
  const jumpToPageRef = useRef<((pageIndex: number) => void) | null>(null);
  const goToPageRef = useRef<(page: number) => void>(() => { });
  const pendingNavigationRef = useRef<number | null>(null);
  const currentPageRef = useRef(currentPage);

  const currentSite = useAppSelector((state) => state.site.currentSite);
  const tenantId = currentSite?.tenant_id;
  const siteId = currentSite?.site_id || currentSite?.id;
  const {
    data: artefactsData,
    isLoading: isLoadingArtefacts,
    error: artefactsError,
  } = useGetSiteArtefactsQuery(
    { tenant_id: tenantId!, site_id: siteId! },
    { skip: !tenantId || !siteId }
  );

  const [getViewUrl, { isLoading: isLoadingViewUrl }] = useGetArtefactViewUrlMutation();

  const asBuiltArtefact = useMemo<Artefact | null>(() => {
    if (!artefactsData?.items?.length) return null;
    // Prefer as-built/drawing documents over other types (e.g. event logs)
    const asBuiltKeywords = ['asbuilt', 'as-built', 'drawing', 'knowledge'];
    const asBuilt = artefactsData.items.find((a) => {
      const combined = `${a.doc_type || ''} ${a.kind || ''}`.toLowerCase();
      return asBuiltKeywords.some((kw) => combined.includes(kw));
    });
    return asBuilt || artefactsData.items[0];
  }, [artefactsData]);

  // Fetch question summary for bounding box overlays (single API call instead of N+1)
  const { data: summaryData, isFetching: isFetchingBBoxes } = useGetQuestionSummaryQuery(
    siteId ? { site_id: siteId, limit: 500, offset: 0 } : undefined,
    { skip: !siteId }
  );

  // Build bounding box map from summary data (grouped by page number)
  const boundingBoxMap = useMemo<Record<number, BoundingBoxOverlay[]>>(() => {
    if (!summaryData?.items) return {};

    const allBoxes: Record<number, BoundingBoxOverlay[]> = {};

    for (const item of summaryData.items) {
      const processDetails = (
        details: Array<{ page_number: number; bounding_box: { x_min?: number; y_min?: number; x_max?: number; y_max?: number } | null; description?: string | null }> | undefined,
        answerType: string
      ) => {
        if (!details) return;
        for (const d of details) {
          const bb = d.bounding_box;
          if (bb && bb.x_min != null && bb.y_min != null && bb.x_max != null && bb.y_max != null) {
            if (!allBoxes[d.page_number]) allBoxes[d.page_number] = [];
            allBoxes[d.page_number].push({
              x_min: bb.x_min,
              y_min: bb.y_min,
              x_max: bb.x_max,
              y_max: bb.y_max,
              answer_type: answerType,
              description: d.description || undefined,
              question_id: item.question_id,
              category: item.category,
            });
          }
        }
      };

      processDetails(item.pass_page_details, 'pass');
      processDetails(item.fail_page_details, 'fail');
      processDetails(item.ca_page_details, 'ca');
    }

    return allBoxes;
  }, [summaryData]);

  const toggleBoundingBoxes = useCallback(() => {
    setShowBoundingBoxes(prev => !prev);
  }, []);

  const pageNavigationPluginRef = useRef(pageNavigationPlugin());
  const pageNavigationPluginInstance = pageNavigationPluginRef.current;
  const { jumpToPage } = pageNavigationPluginInstance;

  const defaultLayoutPluginRef = useRef(defaultLayoutPlugin({
    sidebarTabs: () => [], // Hide sidebar
    toolbarPlugin: {
      fullScreenPlugin: {
        onEnterFullScreen: () => { },
        onExitFullScreen: () => { },
      },

      openFileButton: () => null,
    },
  }));
  const defaultLayoutPluginInstance = defaultLayoutPluginRef.current;

  useEffect(() => {
    jumpToPageRef.current = jumpToPage;

    if (jumpToPage && pendingNavigationRef.current !== null) {
      const pendingPage = pendingNavigationRef.current;
      pendingNavigationRef.current = null;

      let page = pendingPage;
      if (page < 1) page = 1;
      if (totalPages > 0 && page > totalPages) page = totalPages;
      const pageIndex = page - 1;
      jumpToPage(pageIndex);
      setCurrentPage(pageIndex);
    }
  }, [jumpToPage, totalPages]);

  useEffect(() => {
    if (asBuiltArtefact?.meta?.page_refs) {
      setPageRefs(asBuiltArtefact.meta.page_refs as PageRef[]);
    } else if (asBuiltArtefact?.meta?.components) {
      const components = asBuiltArtefact.meta.components as Array<{ label: string; page: number; type?: string }>;
      setPageRefs(components.map(c => ({
        label: c.label,
        page: c.page,
        type: c.type,
      })));
    } else {
      setPageRefs([]);
    }
  }, [asBuiltArtefact]);

  useEffect(() => {
    if (!asBuiltArtefact || !tenantId || !siteId) {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
      setPdfViewUrl(null);
      setIsFromCache(false);
      return;
    }

    let isActive = true;

    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
    setPdfViewUrl(null);
    setIsFromCache(false);

    const fetchViewUrl = async () => {
      try {
        setViewerError(null);

        const cachedBlob = await getCachedPDF(asBuiltArtefact.id);

        if (cachedBlob && isActive) {
          if (blobUrlRef.current) {
            URL.revokeObjectURL(blobUrlRef.current);
          }

          const blobUrl = URL.createObjectURL(cachedBlob);
          blobUrlRef.current = blobUrl;
          setPdfViewUrl(blobUrl);
          setIsFromCache(true);
          return;
        }

        const result = await getViewUrl({
          document_id: asBuiltArtefact.id,
          tenant_id: tenantId,
          expires_in: 3600,
        }).unwrap();

        if (!isActive) return;

        try {
          setDownloadProgress(0);

          const response = await fetch(result.url, {
            method: 'GET',
            mode: 'cors',
            credentials: 'omit',
            headers: {
              'Accept': 'application/pdf',
            },
          });

          if (!response.ok) {
            throw new Error(`Failed to fetch PDF: ${response.statusText}`);
          }

          const contentLength = parseInt(response.headers.get('Content-Length') || '0', 10);
          let blob: Blob;

          if (contentLength > 0 && response.body) {
            // Stream the response to track download progress
            const reader = response.body.getReader();
            const chunks: Uint8Array[] = [];
            let receivedLength = 0;

            while (true) {
              const { done, value } = await reader.read();
              if (done) break;
              chunks.push(value);
              receivedLength += value.length;
              if (isActive) {
                setDownloadProgress(Math.round((receivedLength / contentLength) * 100));
              }
            }

            blob = new Blob(chunks, { type: 'application/pdf' });
          } else {
            // Fallback if Content-Length not available
            blob = await response.blob();
          }

          if (!isActive) return;
          setDownloadProgress(null);

          cachePDF(asBuiltArtefact.id, blob, siteId, tenantId).catch(() => {
            // Silently fail - caching is not critical
          });

          if (blobUrlRef.current) {
            URL.revokeObjectURL(blobUrlRef.current);
          }

          const blobUrl = URL.createObjectURL(blob);
          blobUrlRef.current = blobUrl;
          setPdfViewUrl(blobUrl);
        } catch {
          if (!isActive) return;
          setDownloadProgress(null);
          // Fallback to direct URL on fetch error (e.g., CORS issues)
          setPdfViewUrl(result.url);
        }
      } catch {
        if (!isActive) return;
        setDownloadProgress(null);
        setViewerError('Failed to get document URL');
      }
    };

    fetchViewUrl();

    return () => {
      isActive = false;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [asBuiltArtefact, tenantId, siteId, getViewUrl]);

  const goToPage = useCallback((page: number) => {
    if (page < 1) page = 1;
    if (totalPages > 0 && page > totalPages) page = totalPages;
    const pageIndex = page - 1;
    currentPageRef.current = pageIndex;
    if (jumpToPageRef.current) {
      jumpToPageRef.current(pageIndex);
    } else {
      pendingNavigationRef.current = page;
    }
  }, [totalPages]);

  useEffect(() => {
    goToPageRef.current = goToPage;
  }, [goToPage]);
  useEffect(() => {
    const handleComponentSelect = (event: CustomEvent<{
      page?: number;
      label?: string;
      nodeId?: string;
      nodeLabel?: string;
      pageRefs?: PageRef[];
      boundingBox?: { x_min: number; y_min: number; x_max: number; y_max: number };
    }>) => {
      const detail = event.detail;

      // Handle bounding box highlight
      if (detail?.boundingBox && detail?.page != null) {
        setHighlightedBBox({ pageNumber: detail.page, bbox: detail.boundingBox });
      } else {
        setHighlightedBBox(null);
      }

      // Helper: only scroll if not already on the target page
      const navigateIfNeeded = (page: number) => {
        if (page - 1 !== currentPageRef.current) {
          goToPageRef.current(page);
        }
      };

      if (detail?.nodeId) {
        const refs: PageRef[] = [];

        if (Array.isArray(detail.pageRefs) && detail.pageRefs.length > 0) {
          refs.push(...detail.pageRefs);
        } else if (detail.page != null) {
          refs.push({
            label: detail.nodeLabel || detail.nodeId,
            page: detail.page,
          });
        }

        setSelectedComponent({
          nodeId: detail.nodeId,
          nodeLabel: detail.nodeLabel,
          pageRefs: refs,
        });

        if (refs.length > 0) {
          navigateIfNeeded(refs[0].page);
        } else if (detail.page != null) {
          navigateIfNeeded(detail.page);
        }
      } else if (detail?.page != null) {
        navigateIfNeeded(detail.page);
      } else if (detail?.label && pageRefs.length > 0) {
        const ref = pageRefs.find(r => r.label === detail.label);
        if (ref) {
          navigateIfNeeded(ref.page);
        }
      }
    };

    window.addEventListener('navigateToAsBuiltPage', handleComponentSelect as EventListener);
    return () => {
      window.removeEventListener('navigateToAsBuiltPage', handleComponentSelect as EventListener);
    };
  }, [pageRefs]);

  const clearSelectedComponent = useCallback(() => {
    setSelectedComponent(null);
  }, []);

  const handlePageChange = useCallback((e: { currentPage: number }) => {
    currentPageRef.current = e.currentPage;
    setCurrentPage(e.currentPage);
  }, []);

  const handleDocumentLoad = useCallback((e: { doc: { numPages: number } }) => {
    setTotalPages(e.doc.numPages);

    if (pendingNavigationRef.current !== null) {
      const pendingPage = pendingNavigationRef.current;
      pendingNavigationRef.current = null;

      setTimeout(() => {
        if (jumpToPageRef.current) {
          let page = pendingPage;
          if (page < 1) page = 1;
          if (page > e.doc.numPages) page = e.doc.numPages;
          const pageIndex = page - 1;
          jumpToPageRef.current(pageIndex);
          setCurrentPage(pageIndex);
        }
      }, 100);
    }
  }, []);

  const isLoading = isLoadingArtefacts || isLoadingViewUrl;
  const displayPage = currentPage + 1;

  return {
    currentSite,
    tenantId,
    siteId,
    asBuiltArtefact,
    isLoadingArtefacts,
    artefactsError,
    pdfViewUrl,
    isLoadingViewUrl,
    viewerError,
    isFromCache,
    currentPage,
    totalPages,
    displayPage,
    pageRefs,
    goToPage,
    selectedComponent,
    clearSelectedComponent,
    handlePageChange,
    handleDocumentLoad,
    defaultLayoutPluginInstance,
    pageNavigationPluginInstance,
    boundingBoxMap,
    showBoundingBoxes,
    toggleBoundingBoxes,
    isFetchingBBoxes,
    highlightedBBox,
    downloadProgress,
    isLoading,
  };
}
